import { supabase } from '../lib/supabase';
import { mockShopifyProducts, isDevelopmentMode, simulateNetworkDelay } from './mockApi';

// Récupérer les variables d'environnement de Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

interface ShopifyConfig {
  shopDomain: string;
  accessToken: string;
}

interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  currency: string;
  images: { src: string }[];
  variants: any[];
  status: string;
  vendor: string;
  product_type: string;
  tags: string[];
}

export class ShopifyService {
  private config: ShopifyConfig;
  private chatbotPublicId: string;
  private baseUrl: string;

  constructor(config: ShopifyConfig, chatbotPublicId: string) {
    // Nettoyer le domaine en supprimant https:// ou http:// si présent
    const cleanDomain = config.shopDomain.replace(/^https?:\/\//, '').trim();
    
    this.config = {
      ...config,
      shopDomain: cleanDomain
    };
    this.chatbotPublicId = chatbotPublicId;
    this.baseUrl = `https://${cleanDomain}/admin/api/2024-01`;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      // Vérifier si nous sommes en mode développement
      const inDevMode = isDevelopmentMode();
      console.log(`isDevelopmentMode() returned: ${inDevMode}`, { 
        hostname: window.location.hostname,
        url: window.location.href,
        env: import.meta.env.MODE
      });
      
      // S'assurer que l'endpoint commence par / s'il ne l'est pas déjà
      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      
      // En mode développement, utiliser une approche alternative pour contourner CORS
      if (inDevMode) {
        console.log('Development mode detected, using local API simulation');
        
        // Pour les requêtes de produits, nous pouvons simuler une réponse basée sur des données mockées
        if (normalizedEndpoint.includes('/products.json')) {
          console.log('Simulating Shopify products API response in development mode');
          
          // Simuler un délai réseau
          await simulateNetworkDelay();
          
          // Retourner des données mockées pour le développement
          return {
            products: mockShopifyProducts
          } as T;
        }
        
        // Pour d'autres endpoints, nous pouvons tenter d'utiliser l'API Netlify déployée
        console.log('Falling back to deployed Netlify function for this endpoint');
      }
      
      // *** TOUJOURS UTILISER L'API RÉELLE EN PRODUCTION ***
      if (!inDevMode) {
        console.log('PRODUCTION MODE: Using real Shopify API via Edge Function');
      }
      
      // Utiliser l'Edge Function Supabase pour le proxy Shopify
      // Extraire le project ID de l'URL Supabase pour construire l'URL de la fonction
      const projectId = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'ddounabnvgpiszavsudj';
      const supabaseEdgeFunction = `https://${projectId}.supabase.co/functions/v1/shopify-proxy`;
      
      console.log(`Making request to ${normalizedEndpoint} via Supabase Edge Function`);
      
      try {
        const response = await fetch(supabaseEdgeFunction, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({
            endpoint: normalizedEndpoint,
            shopDomain: this.config.shopDomain,
            accessToken: this.config.accessToken,
            method: options.method || 'GET',
            body: options.body,
            headers: options.headers
          })
        });

        if (!response.ok) {
          // Tenter de lire le corps de la réponse pour obtenir plus de détails sur l'erreur
          let errorDetails = '';
          try {
            const errorBody = await response.json();
            errorDetails = JSON.stringify(errorBody);
          } catch (e) {
            errorDetails = response.statusText;
          }
          throw new Error(`Shopify API error (${response.status}): ${errorDetails}`);
        }

        // Vérifier que la réponse n'est pas un objet mock
        const data = await response.json();
        console.log(`Received response from Edge Function:`, data);
        
        // Si la réponse contient des produits, vérifier qu'il ne s'agit pas de mocks
        if (data.products && data.products.length > 0) {
          const firstProduct = data.products[0];
          if (firstProduct.title && firstProduct.title.startsWith('Mock Product')) {
            console.error('WARNING: Received mock products in production environment!');
          }
        }

        return data;
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        throw fetchError;
      }
    } catch (error) {
      console.error('Error in makeRequest:', error);
      throw error;
    }
  }

  async syncProducts(): Promise<void> {
    try {
      // Nouvelle méthode de pagination utilisant des curseurs au lieu des pages
      let hasNextPage = true;
      const limit = 50; // Récupérer 50 produits à la fois
      let cursor = '';

      while (hasNextPage) {
        // Construire l'endpoint avec la pagination par curseur
        let endpoint = `/products.json?limit=${limit}`;
        
        // Ajouter le curseur si nous ne sommes pas à la première page
        if (cursor) {
          endpoint += `&page_info=${cursor}`;
        }
        
        // Appeler l'API Shopify
        const response = await this.makeRequest<{
          products: ShopifyProduct[];
          next_page_url?: string;
        }>(endpoint);

        // Traiter chaque produit
        for (const product of response.products) {
          await this.upsertProduct(product);
        }

        // Vérifier s'il y a une page suivante
        if (response.next_page_url) {
          // Extraire le curseur de l'URL de la page suivante
          const match = response.next_page_url.match(/page_info=([^&]+)/);
          if (match && match[1]) {
            cursor = match[1];
            hasNextPage = true;
          } else {
            hasNextPage = false;
          }
        } else {
          hasNextPage = false;
        }
      }
    } catch (err) {
      console.error('Error syncing Shopify products:', err);
      throw err;
    }
  }

  private async upsertProduct(shopifyProduct: ShopifyProduct): Promise<void> {
    try {
      // Get the main variant for price and inventory
      const mainVariant = shopifyProduct.variants[0] || {};
      const price = parseFloat(mainVariant.price || shopifyProduct.price || '0');
      const inventory = mainVariant.inventory_quantity || 0;
      const sku = mainVariant.sku || '';
      
      // Get the first image if available
      const imageUrl = shopifyProduct.images && shopifyProduct.images.length > 0 
        ? shopifyProduct.images[0].src 
        : null;

      // Calculate position based on existing products
      const { data: existingProducts } = await supabase
        .from('products')
        .select('position')
        .eq('chatbot_public_id', this.chatbotPublicId)
        .order('position', { ascending: false })
        .limit(1);

      const maxPosition = existingProducts && existingProducts.length > 0 
        ? existingProducts[0].position + 1 
        : 0;

      // S'assurer que tags est toujours un tableau valide et non une chaîne vide
      let productTags: string[] = [];
      if (Array.isArray(shopifyProduct.tags)) {
        productTags = shopifyProduct.tags;
      } 

      // S'assurer que additional_images est un tableau valide
      const additionalImages = shopifyProduct.images && shopifyProduct.images.length > 1
        ? shopifyProduct.images.slice(1).map(img => img.src)
        : [];

      const { error } = await supabase
        .from('products')
        .upsert({
          chatbot_public_id: this.chatbotPublicId,
          name: shopifyProduct.title,
          description: shopifyProduct.description,
          price: price,
          currency: 'USD', // Default to USD, can be customized
          image_url: imageUrl,
          category: shopifyProduct.product_type || null,
          tags: productTags,
          sku: sku,
          stock: inventory,
          source: 'shopify',
          external_id: shopifyProduct.id,
          position: maxPosition,
          metadata: {
            vendor: shopifyProduct.vendor || '',
            status: shopifyProduct.status || 'active',
            additional_images: additionalImages
          }
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error upserting product:', err);
      throw err;
    }
  }

  async handleWebhook(topic: string, payload: any): Promise<void> {
    try {
      switch (topic) {
        case 'products/create':
        case 'products/update': {
          await this.upsertProduct(payload);
          break;
        }
        case 'products/delete': {
          const { error } = await supabase
            .from('products')
            .delete()
            .eq('external_id', payload.id)
            .eq('chatbot_public_id', this.chatbotPublicId);

          if (error) throw error;
          break;
        }
      }
    } catch (err) {
      console.error('Error handling Shopify webhook:', err);
      throw err;
    }
  }

  async verifyWebhook(topic: string, payload: any, hmac: string): Promise<boolean> {
    try {
      const message = JSON.stringify(payload);
      const generated = await this.generateHmac(message, this.config.accessToken);
      return hmac === generated;
    } catch (err) {
      console.error('Error verifying Shopify webhook:', err);
      return false;
    }
  }

  private async generateHmac(message: string, secret: string): Promise<string> {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(secret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(message)
    );

    return Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async registerWebhook(topic: string, address: string): Promise<void> {
    try {
      // Vérifier si nous sommes en mode développement
      const inDevMode = isDevelopmentMode();

      if (inDevMode) {
        console.log(`[DEV MODE] Simulating webhook registration for ${topic} to ${address}`);
        return;
      }

      const response = await this.makeRequest<any>('/webhooks.json', {
        method: 'POST',
        body: JSON.stringify({
          webhook: {
            topic,
            address,
            format: 'json'
          }
        })
      });

      console.log(`Registered Shopify webhook for ${topic}:`, response);
    } catch (err) {
      console.error(`Error registering Shopify webhook for ${topic}:`, err);
      throw err;
    }
  }

  async listWebhooks(): Promise<any[]> {
    try {
      const response = await this.makeRequest<{ webhooks: any[] }>('/webhooks.json');
      return response.webhooks;
    } catch (err) {
      console.error('Error listing Shopify webhooks:', err);
      throw err;
    }
  }

  async deleteWebhook(webhookId: string): Promise<void> {
    try {
      await this.makeRequest(`/webhooks/${webhookId}.json`, {
        method: 'DELETE'
      });
    } catch (err) {
      console.error(`Error deleting Shopify webhook ${webhookId}:`, err);
      throw err;
    }
  }
}